/**
 * ClientSelector — Dropdown buscable de clientes en el sidebar
 *
 * Patrón estilo Monday/Linear: click → popover con search → seleccionar → todas
 * las páginas se filtran a ese cliente. "Todos los clientes" resetea.
 *
 * Atajo: Ctrl+L para abrir el selector (futuro)
 */
import { useState, useMemo, useRef, useEffect } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Command } from "cmdk";
import { Building2, Search, Check, ChevronDown, X, Users } from "../lib/icons.jsx";
import { cn, initials } from "../lib/utils.js";
import { useClient } from "../context/ClientContext.jsx";
import { Avatar, AvatarImage, AvatarFallback } from "./ui/avatar.jsx";

/** Categoría helper para el chip A-G */
function categoriaLetter(cat) {
  if (!cat) return null;
  const m = String(cat).match(/Categor[íi]a?\s*([A-G])/i);
  return m ? m[1].toUpperCase() : null;
}
const CAT_COLOR = {
  A: "bg-[#8B5CF6]/15 text-[#5B21B6]",
  B: "bg-[#0E7490]/15   text-[#155E75]",
  C: "bg-[#0E7490]/15   text-[#155E75]",
  D: "bg-[#F59E0B]/15 text-[#92400E]",
  E: "bg-[#8B5CF6]/15   text-[#5B21B6]",
  F: "bg-[#F59E0B]/15  text-[#92400E]",
  G: "bg-[#DC2626]/15   text-[#B91C1C]",
};

export function ClientSelector({ collapsed }) {
  const { clientes, selectedId, selectedCliente, setSelected, loading } = useClient();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  /* Solo activos, ordenados por nombre */
  const activos = useMemo(
    () => clientes.filter(c => c.estado === "activo").sort((a, b) => a.nombre.localeCompare(b.nombre)),
    [clientes]
  );

  function handleSelect(id) {
    setSelected(id);
    setOpen(false);
    setQuery("");
  }

  /* Reset query al cerrar */
  useEffect(() => { if (!open) setQuery(""); }, [open]);

  /* ── Render trigger (colapsado vs expandido) ──────────────────────────── */
  const trigger = collapsed ? (
    // Colapsado: solo avatar + chevron
    <button className="w-full flex justify-center py-2.5 hover:bg-muted rounded-2xl transition-colors">
      {selectedCliente ? (
        <Avatar className="h-9 w-9">
          {selectedCliente.logo_url && <AvatarImage src={selectedCliente.logo_url} alt=""/>}
          <AvatarFallback className="bg-night text-white text-[12px] font-extrabold">
            {initials(selectedCliente.nombre)}
          </AvatarFallback>
        </Avatar>
      ) : (
        <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center">
          <Users className="h-[18px] w-[18px] text-muted-foreground" />
        </div>
      )}
    </button>
  ) : (
    // Expandido: avatar + nombre + categoría + chevron
    <button className={cn(
      "w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl",
      "hover:bg-muted/70",
      "transition-all group/sel"
    )}>
      {selectedCliente ? (
        <>
          <Avatar className="h-9 w-9 flex-shrink-0">
            {selectedCliente.logo_url && <AvatarImage src={selectedCliente.logo_url} alt=""/>}
            <AvatarFallback className="bg-night text-white text-[12px] font-extrabold">
              {initials(selectedCliente.nombre)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0 text-left">
            <p className="text-[15px] font-extrabold text-foreground truncate leading-tight">
              {selectedCliente.nombre}
            </p>
            <p className="text-[12px] text-muted-foreground truncate leading-tight">
              {categoriaLetter(selectedCliente.categoria)
                ? `Cat ${categoriaLetter(selectedCliente.categoria)}${selectedCliente.industria ? " · " + selectedCliente.industria : ""}`
                : (selectedCliente.industria ?? "Cliente")}
            </p>
          </div>
        </>
      ) : (
        <>
          <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
            <Users className="h-[18px] w-[18px] text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0 text-left">
            <p className="text-[15px] font-extrabold text-foreground leading-tight">Todos los clientes</p>
            <p className="text-[12px] text-muted-foreground leading-tight">
              {activos.length} activo{activos.length !== 1 && "s"}
            </p>
          </div>
        </>
      )}
      <ChevronDown className="h-5 w-5 text-muted-foreground/70 flex-shrink-0 group-hover/sel:text-foreground" />
    </button>
  );

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        {trigger}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className={cn(
            "z-[60] w-72 sm:w-80 rounded-3xl bg-popover shadow-[0_24px_60px_rgba(17,19,24,0.22)] overflow-hidden",
            "data-[state=open]:animate-fade-in"
          )}
        >
          <Command className="bg-transparent" shouldFilter loop>
            {/* Search input */}
            <div className="flex items-center gap-2 px-4 border-b border-[#F2F3F6]">
              <Search className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              <Command.Input
                value={query}
                onValueChange={setQuery}
                placeholder="Buscar cliente…"
                className="flex h-12 w-full bg-transparent text-[15px] font-semibold text-foreground placeholder:text-muted-foreground outline-none"
              />
              {query && (
                <button onClick={() => setQuery("")} className="text-muted-foreground hover:text-foreground">
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>

            <Command.List className="max-h-80 overflow-y-auto p-1.5">
              <Command.Empty className="py-6 text-center text-xs text-muted-foreground">
                Sin clientes para "{query}"
              </Command.Empty>

              {/* All clients pseudo-option */}
              <Command.Group className={groupCls}>
                <Command.Item
                  value="todos los clientes ver todo"
                  onSelect={() => handleSelect(null)}
                  className={itemCls}
                >
                  <div className="h-6 w-6 rounded-full bg-muted/40 flex items-center justify-center flex-shrink-0">
                    <Users className="h-3 w-3 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-foreground leading-tight">Todos los clientes</p>
                    <p className="text-[12px] text-muted-foreground leading-tight">Vista agregada</p>
                  </div>
                  {!selectedId && <Check className="h-3.5 w-3.5 text-accent" />}
                </Command.Item>
              </Command.Group>

              <Command.Separator className="my-1 h-px bg-border/30 mx-1" />

              {/* Active clients */}
              <Command.Group
                heading={`Activos · ${activos.length}`}
                className={groupCls}
              >
                {activos.map(c => {
                  const cat = categoriaLetter(c.categoria);
                  return (
                    <Command.Item
                      key={c.id}
                      value={`${c.nombre} ${c.categoria ?? ""} ${c.industria ?? ""}`}
                      onSelect={() => handleSelect(c.id)}
                      className={itemCls}
                    >
                      <Avatar className="h-9 w-9 flex-shrink-0">
                        {c.logo_url && <AvatarImage src={c.logo_url} alt=""/>}
                        <AvatarFallback className="bg-night text-white text-[12px] font-extrabold">
                          {initials(c.nombre)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground truncate leading-tight">{c.nombre}</p>
                        {c.industria && (
                          <p className="text-[12px] text-muted-foreground truncate leading-tight">{c.industria}</p>
                        )}
                      </div>
                      {cat && (
                        <span className={cn(
                          "text-[12px] font-bold px-1.5 py-0.5 rounded-md flex-shrink-0",
                          CAT_COLOR[cat] ?? "bg-[#6B6F7A]/15 text-foreground"
                        )}>
                          {cat}
                        </span>
                      )}
                      {selectedId === c.id && <Check className="h-3.5 w-3.5 text-accent flex-shrink-0" />}
                    </Command.Item>
                  );
                })}
              </Command.Group>

              {loading && (
                <div className="py-3 text-center text-[12px] text-muted-foreground">
                  Cargando clientes…
                </div>
              )}
            </Command.List>

            <div className="border-t border-border px-3 py-1.5 text-[12px] text-muted-foreground/60 flex items-center justify-between">
              <span>↑↓ navegar · ↵ seleccionar</span>
              <span>{activos.length} cliente{activos.length !== 1 && "s"}</span>
            </div>
          </Command>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

const groupCls = [
  "[&_[cmdk-group-heading]]:px-2",
  "[&_[cmdk-group-heading]]:py-1",
  "[&_[cmdk-group-heading]]:text-[12px]",
  "[&_[cmdk-group-heading]]:font-semibold",
  "[&_[cmdk-group-heading]]:uppercase",
  "[&_[cmdk-group-heading]]:tracking-widest",
  "[&_[cmdk-group-heading]]:text-muted-foreground/50",
  "[&_[cmdk-group-heading]]:select-none",
].join(" ");

const itemCls = [
  "flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer",
  "text-foreground/80 transition-colors",
  "data-[selected=true]:bg-accent/10 data-[selected=true]:text-foreground",
  "outline-none",
].join(" ");
